import { useState } from 'react';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import type { WatchBrand } from '../../types';

interface BrandFilterSelectProps {
  /** Brand list — expected pre-sorted (e.g. alphabetically) by the caller. */
  brands: WatchBrand[];
  /** When true, the select is disabled and shows a loading placeholder. */
  loading: boolean;
  /** Currently selected brand id ('' for none). */
  value: string;
  /** Called with the newly selected brand id ('' when cleared). */
  onChange: (brandId: string) => void;
  /** Field label (omit to render no label). */
  label?: string;
  /** Optional className for the wrapping element. */
  className?: string;
}

/**
 * Brand picker with a search/filter input above a native <select>.
 *
 * Shared by the Calibre Enrichment and Occasion bulk-tagger flows so both
 * surfaces stay in parity. Single-select; the filter term is local component
 * state — callers only own the selected brand id.
 *
 * Matching is Algolia's (typo-tolerant, prefix, accent-folded, and across
 * alternative names / abbreviations / parent org), with the old substring filter
 * over `brands` as the offline fallback. Callers still pass the full brand list:
 * it is both the unsearched list and the source of the option labels.
 */
export function BrandFilterSelect({
  brands,
  loading,
  value,
  onChange,
  label = 'Brand',
  className,
}: BrandFilterSelectProps) {
  const [search, setSearch] = useState('');
  const { results: filteredBrands } = useBrandSearch(brands, search);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search brands…"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
      />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
      >
        <option value="">
          {loading ? 'Loading brands…' : `Select a brand… (${filteredBrands.length} match)`}
        </option>
        {filteredBrands.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
    </div>
  );
}
